import { RowCrudStatus } from "../../common/types";
import {
  Publisher,
  PublisherCreateMutationVariables,
  PublisherUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { PersonType } from "../../../api/graphql";
import { MaritalStatus } from "../../../api/graphql";
import { countryDisplayIdByDemonym } from "../../country/store/CountryCommonStore";
import { blankToNull } from "../../../common/blankToNull";
import { countryDisplayId } from "../../country/store/CountryCommonStore";
import { provinceStateDisplayId } from "../../provinceState/store/ProvinceStateCommonStore";
import {
  PublisherRepresentativeValuesType,
  getPublisherRepresentativeValues,
  getPublisherRepresentativeCreateInput,
  getPublisherRepresentativeUpdateInput,
} from "../../publisherRepresentative/store/PublisherRepresentativeCommonStore";
import {
  addedStatusList,
  originalDeletedStatusList,
  originalUpdatedStatusList,
} from "../../common/types";
import {
  PublisherCollectingSocietyValuesType,
  getPublisherCollectingSocietyValues,
  getPublisherCollectingSocietyCreateInput,
  getPublisherCollectingSocietyUpdateInput,
} from "../../publisherCollectingSociety/store/PublisherCollectingSocietyCommonStore";

export type PublisherValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  personType: PersonType;
  name: string;
  maritalStatus: MaritalStatus | "";
  __nationality: string;
  showNationality: { displayId: string; id: string } | null;
  pseudonym: string;
  taxId: string;
  phone: string;
  email: string;
  streetAndNumber: string;
  floor: string;
  suite: string;
  postalCode: string;
  __country: string;
  showCountry: { displayId: string; id: string } | null;
  __province: string;
  showProvince: { displayId: string; id: string } | null;
  city: string;
  caeIpi: string;
  showRepresentatives: PublisherRepresentativeValuesType[];
  showManagementSocieties: PublisherCollectingSocietyValuesType[];
  createdAt_: Date | null;
  lastUpdatedAt_: Date | null;
};

export type PublisherErrorsType = {
  personType?: { message: string; fieldLabel: string };
  name?: { message: string; fieldLabel: string };
  maritalStatus?: { message: string; fieldLabel: string };
  nationality?: { message: string; fieldLabel: string };
  pseudonym?: { message: string; fieldLabel: string };
  taxId?: { message: string; fieldLabel: string };
  phone?: { message: string; fieldLabel: string };
  email?: { message: string; fieldLabel: string };
  streetAndNumber?: { message: string; fieldLabel: string };
  floor?: { message: string; fieldLabel: string };
  suite?: { message: string; fieldLabel: string };
  postalCode?: { message: string; fieldLabel: string };
  country?: { message: string; fieldLabel: string };
  province?: { message: string; fieldLabel: string };
  city?: { message: string; fieldLabel: string };
  caeIpi?: { message: string; fieldLabel: string };
  representatives?: { message: string; fieldLabel: string };
  managementSocieties?: { message: string; fieldLabel: string };
};

export function PublisherValuesInit(): PublisherValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    personType: "" as PersonType,
    name: "",
    maritalStatus: "" as MaritalStatus | "",
    __nationality: "",
    showNationality: null,
    pseudonym: "",
    taxId: "",
    phone: "",
    email: "",
    streetAndNumber: "",
    floor: "",
    suite: "",
    postalCode: "",
    __country: "",
    showCountry: null,
    __province: "",
    showProvince: null,
    city: "",
    caeIpi: "",
    showRepresentatives: [],
    showManagementSocieties: [],
    createdAt_: null,
    lastUpdatedAt_: null,
  };
}

export type PublisherType = Publisher | PublisherValuesType;

export function publisherDisplayId(
  source: Partial<Publisher>,
  _langStore: LangStore
) {
  return ((): string => {
    const __p = (source.pseudonym ?? "").trim();
    return __p ? __p : source.name ?? _langStore.emptyStringSymbol;
  })();
}

export function getPublisherValues(
  source: PublisherType,
  _langStore: LangStore,
  _duplicated: boolean = false
): PublisherValuesType {
  const entityValues = PublisherValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : publisherDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.personType = source.personType;
  entityValues.name = source.name;
  entityValues.maritalStatus =
    source.maritalStatus || ("" as MaritalStatus | "");
  entityValues.showNationality =
    "nationality" in source && source.nationality
      ? {
          id: source.nationality.id,
          displayId: countryDisplayIdByDemonym(source.nationality, _langStore),
        }
      : "showNationality" in source && source.showNationality
      ? {
          id: source.showNationality.id,
          displayId: source.showNationality.displayId,
        }
      : null;
  entityValues.__nationality = entityValues.showNationality?.displayId || "";
  entityValues.pseudonym = source.pseudonym || "";
  entityValues.taxId = source.taxId;
  entityValues.phone = source.phone;
  entityValues.email = source.email;
  entityValues.streetAndNumber = source.streetAndNumber;
  entityValues.floor = source.floor || "";
  entityValues.suite = source.suite || "";
  entityValues.postalCode = source.postalCode;
  entityValues.showCountry =
    "country" in source && source.country
      ? {
          id: source.country.id,
          displayId: countryDisplayId(source.country, _langStore),
        }
      : "showCountry" in source && source.showCountry
      ? { id: source.showCountry.id, displayId: source.showCountry.displayId }
      : null;
  entityValues.__country = entityValues.showCountry?.displayId || "";
  entityValues.showProvince =
    "province" in source && source.province
      ? {
          id: source.province.id,
          displayId: provinceStateDisplayId(source.province, _langStore),
        }
      : "showProvince" in source && source.showProvince
      ? { id: source.showProvince.id, displayId: source.showProvince.displayId }
      : null;
  entityValues.__province = entityValues.showProvince?.displayId || "";
  entityValues.city = source.city;
  entityValues.caeIpi = source.caeIpi;
  entityValues.showRepresentatives =
    "representatives" in source && source.representatives
      ? (source.representatives || []).map((x) => {
          const row: PublisherRepresentativeValuesType =
            getPublisherRepresentativeValues(x, _langStore, _duplicated);
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showRepresentatives" in source && source.showRepresentatives
      ? (source.showRepresentatives || []).map((x) => {
          const row: PublisherRepresentativeValuesType =
            getPublisherRepresentativeValues(x, _langStore, _duplicated);
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  entityValues.showManagementSocieties =
    "managementSocieties" in source && source.managementSocieties
      ? (source.managementSocieties || []).map((x) => {
          const row: PublisherCollectingSocietyValuesType =
            getPublisherCollectingSocietyValues(x, _langStore, _duplicated);
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showManagementSocieties" in source && source.showManagementSocieties
      ? (source.showManagementSocieties || []).map((x) => {
          const row: PublisherCollectingSocietyValuesType =
            getPublisherCollectingSocietyValues(x, _langStore, _duplicated);
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  entityValues.createdAt_ = source.createdAt_
    ? new Date(source.createdAt_)
    : null;
  entityValues.lastUpdatedAt_ = source.lastUpdatedAt_
    ? new Date(source.lastUpdatedAt_)
    : null;
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getPublisherUpdateVariables(
  values: PublisherValuesType
): PublisherUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    personType: values.personType,
    name: values.name,
    maritalStatus: values.maritalStatus || null,
    nationalityId: values.showNationality?.id || "",
    pseudonym: blankToNull(values.pseudonym),
    taxId: values.taxId,
    phone: values.phone,
    email: values.email,
    streetAndNumber: values.streetAndNumber,
    floor: blankToNull(values.floor),
    suite: blankToNull(values.suite),
    postalCode: values.postalCode,
    countryId: values.showCountry?.id || "",
    provinceId: values.showProvince?.id || "",
    city: values.city,
    caeIpi: values.caeIpi,
    representatives: {
      create: values.showRepresentatives
        .filter((x) => addedStatusList.includes(x.__status__))
        .map((x) => getPublisherRepresentativeCreateInput(x)),
      update: values.showRepresentatives
        .filter((x) => originalUpdatedStatusList.includes(x.__status__))
        .map((x) => getPublisherRepresentativeUpdateInput(x)),
      remove: values.showRepresentatives
        .filter((x) => originalDeletedStatusList.includes(x.__status__))
        .map((x) => x.id),
    },
    managementSocieties: {
      create: values.showManagementSocieties
        .filter((x) => addedStatusList.includes(x.__status__))
        .map((x) => getPublisherCollectingSocietyCreateInput(x)),
      update: values.showManagementSocieties
        .filter((x) => originalUpdatedStatusList.includes(x.__status__))
        .map((x) => getPublisherCollectingSocietyUpdateInput(x)),
      remove: values.showManagementSocieties
        .filter((x) => originalDeletedStatusList.includes(x.__status__))
        .map((x) => x.id),
    },
  };
}

export function getPublisherCreateVariables(
  values: PublisherValuesType
): PublisherCreateMutationVariables {
  return {
    personType: values.personType,
    name: values.name,
    maritalStatus: values.maritalStatus || null,
    nationalityId: values.showNationality?.id || "",
    pseudonym: blankToNull(values.pseudonym),
    taxId: values.taxId,
    phone: values.phone,
    email: values.email,
    streetAndNumber: values.streetAndNumber,
    floor: blankToNull(values.floor),
    suite: blankToNull(values.suite),
    postalCode: values.postalCode,
    countryId: values.showCountry?.id || "",
    provinceId: values.showProvince?.id || "",
    city: values.city,
    caeIpi: values.caeIpi,
    representatives: values.showRepresentatives
      .filter((x) => addedStatusList.includes(x.__status__))
      .map((x) => getPublisherRepresentativeCreateInput(x)),
    managementSocieties: values.showManagementSocieties
      .filter((x) => addedStatusList.includes(x.__status__))
      .map((x) => getPublisherCollectingSocietyCreateInput(x)),
  };
}
